export type TypeaheadOptionSectionLabels = {
  /** Header for org free-option / user-created entries (shown first). */
  custom?: string;
  /** Header for API / reference-data results (shown second). */
  system?: string;
};

export type ResolvedTypeaheadSectionLabels = {
  custom: string;
  system: string;
};

const SECTION_ORDER = {
  CUSTOM: "0",
  SYSTEM: "1",
} as const;

export function resolveTypeaheadSectionLabels(
  fieldLabel: string,
  labels?: TypeaheadOptionSectionLabels,
): ResolvedTypeaheadSectionLabels {
  const trimmed = fieldLabel?.trim() || "Options";

  return {
    custom: labels?.custom ?? `Custom ${trimmed}`,
    system: labels?.system ?? trimmed,
  };
}

export function isCustomTypeaheadOption(option: unknown): boolean {
  const record = option as Record<string, unknown>;
  // Org free-options are tagged with isOrgFreeOption and a Mongo `id` when merged
  // into the list. Creatable "Add …" rows use isCreatable. (API SNOMED hits use
  // `_id`, not `id`, so bare `id` is safe to treat as custom.)
  return Boolean(record?.isOrgFreeOption || record?.isCreatable || record?.id);
}

export function getTypeaheadOptionGroupKey(
  option: unknown,
  sectionLabels: { custom: string; system: string },
): string {
  const isCustom = isCustomTypeaheadOption(option);
  const order = isCustom ? SECTION_ORDER.CUSTOM : SECTION_ORDER.SYSTEM;
  const label = isCustom ? sectionLabels.custom : sectionLabels.system;
  return `${order}|${label}`;
}

export function parseTypeaheadGroupLabel(groupKey: string): string {
  const pipeIndex = groupKey.indexOf("|");
  return pipeIndex >= 0 ? groupKey.slice(pipeIndex + 1) : groupKey;
}

/**
 * Stable unique key for Autocomplete options. MUI defaults to getOptionLabel,
 * which collides when multiple meds/SNOMED rows share a display name and causes
 * duplicate React keys (stuck/stacking list items across searches).
 */
export function getTypeaheadOptionKey(option: unknown): string | number {
  if (option == null || typeof option !== "object") {
    return String(option ?? "");
  }

  const record = option as Record<string, unknown>;

  const candidates = [
    record.id,
    record._id,
    record.medicationCode,
    record.snomedCID,
    record.code,
    typeof record.value === "string" || typeof record.value === "number"
      ? record.value
      : null,
  ];

  for (const candidate of candidates) {
    if (candidate != null && String(candidate).trim() !== "") {
      return String(candidate);
    }
  }

  const label =
    record.snomedFSN ??
    record.display ??
    record.medicineName ??
    record.text ??
    "";
  // Last resort: include form/product so same-name meds still differ when possible.
  const disambiguator = [record.form, record.productType, record.genericName]
    .filter((part) => typeof part === "string" && part.trim())
    .join("|");

  return disambiguator ? `${label}|${disambiguator}` : String(label);
}
